<template>
  <HrPageLayout
    title="Imprest Approval"
    subtitle="Review and approve imprest requests"
    icon="✓"
    :breadcrumb-items="[
      { label: 'Imprest' },
      { label: 'Approval' }
    ]"
  >
    <div class="imprest-approval-content">
      <!-- Stats -->
      <div class="hr-metrics-grid hr-mb-4">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-file"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Requests</p>
          <h2 class="hr-metric-value">{{ totalRequests }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-time-five"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Pending Approval</p>
          <h2 class="hr-metric-value">{{ pendingApprovals }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-money"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Total Amount</p>
          <h2 class="hr-metric-value">{{ formatPrice(totalPendingAmount) }}</h2>
            </div>
        </div>
        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-line-chart"></i></div>
          <div class="hr-metric-content">
              <p class="hr-metric-label">Average Amount</p>
          <h2 class="hr-metric-value">{{ formatPrice(totalPendingAmount / (pendingApprovals || 1)) }}</h2>
            </div>
        </div>
      </div>

      <!-- Table -->
      <div class="hr-chart-card">
        <div class="hr-chart-header hr-flex-between hr-flex-wrap">
          <h5 class="hr-chart-title">Imprest Requests Pending Approval</h5>
          <div class="hr-flex hr-gap-2 hr-flex-wrap">
            <div class="hr-search-wrap">
              <input type="text" class="hr-input" v-model="search" placeholder="Search..." @input="onSearchInput">
              <i class="bx bx-search-alt search-icon"></i>
            </div>
            <div class="hr-flex hr-gap-2">
              <input type="date" class="hr-input" v-model="startDate" style="max-width: 140px;">
              <input type="date" class="hr-input" v-model="endDate" style="max-width: 140px;">
              <button class="hr-btn hr-btn-primary" @click="getImprestRequests()" :disabled="isLoading">
                <i class="bx bx-filter-alt"></i> Filter
                <span v-if="isLoading" class="hr-spinner-sm"></span>
              </button>
            </div>
          </div>
        </div>
        <div class="hr-chart-body">
          <div v-if="isLoading" class="hr-loading-wrap">
            <div class="hr-spinner"></div>
            <p>Loading imprest requests...</p>
          </div>
          <div v-else-if="imprestRequests.length === 0" class="hr-empty-state">
            <div class="hr-empty-icon"><i class="bx bx-file"></i></div>
            <h4>No imprest requests found</h4>
            <p class="hr-text-muted">Try changing your search or filter criteria</p>
          </div>
          <div v-else class="hr-table-wrapper">
            <table class="hr-table hr-table-hover">
              <thead>
              <tr>
                <th>Request ID</th>
                <th>Purpose</th>
                <th>Request By</th>
                <th>Department</th>
                <th>Amount (KES)</th>
                <th>Requested Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="request in imprestRequests" :key="request.request_id">
                <td>{{ request.request_id }}</td>
                <td><div style="max-width: 200px;">{{ request.purpose }}</div></td>
                <td>{{ request.names }}</td>
                <td>{{ request.department }}</td>
                <td>{{ formatPrice(request.amount) }}</td>
                <td>{{ formatDate(request.requestedDate) }}</td>
                <td><span :class="getStatusBadgeClass(request.status)">{{ request.status }}</span></td>
                <td>
                  <div class="hr-btn-group">
                    <button :disabled="request.status.toLowerCase() !== 'pending'" @click="approveRequest(request)" class="hr-btn hr-btn-sm hr-btn-success">
                      <i class="bx bx-check hr-me-1"></i>Approve
                    </button>
                    <button :disabled="request.status.toLowerCase() !== 'pending'" @click="rejectRequest(request)" class="hr-btn hr-btn-sm hr-btn-danger">
                      <i class="bx bx-x hr-me-1"></i>Reject
                    </button>
                  </div>
                </td>
              </tr>
              </tbody>
            </table>
          </div>
          <div v-if="imprestRequests.length > 0" class="hr-pagination-wrap hr-mt-3">
            <div class="hr-pagination-info">Showing <strong>{{ imprestRequests.length }}</strong> of {{ totalRequests }} entries</div>
            <nav v-if="totalPages > 1" class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="currentPage === 1" @click="changePage(currentPage - 1)">
                <i class="bx bx-chevron-left"></i>
              </button>
              <template v-for="page in displayPages" :key="page">
                <button v-if="page !== '...'" type="button" class="hr-page-btn" :class="{ 'hr-page-active': currentPage === page }" @click="changePage(page)">{{ page }}</button>
                <span v-else class="hr-page-ellipsis">...</span>
              </template>
              <button type="button" class="hr-page-btn" :disabled="currentPage === totalPages" @click="changePage(currentPage + 1)">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Confirmation Modal -->
    <div v-if="showModal" class="hr-modal-overlay" @click.self="closeModal">
      <div class="hr-modal-dialog-sm">
        <div class="hr-modal-content">
          <div class="hr-modal-header hr-flex-between">
            <h5 class="hr-modal-title">{{ modalTitle }}</h5>
            <button type="button" class="hr-modal-close" @click="closeModal">×</button>
          </div>
          <div class="hr-modal-body">
            <p>{{ modalMessage }}</p>
            <div v-if="modalAction === 'reject'" class="hr-mt-3">
              <label class="hr-form-label">Reason for rejection</label>
              <textarea class="hr-textarea" v-model="rejectionReason" rows="3" placeholder="Please provide a reason for rejecting this request"></textarea>
            </div>
          </div>
          <div class="hr-modal-footer hr-flex hr-gap-2 hr-justify-end">
            <button type="button" class="hr-btn hr-btn-secondary" @click="closeModal">Cancel</button>
            <button type="button" :class="modalAction === 'approve' ? 'hr-btn hr-btn-success' : 'hr-btn hr-btn-danger'" @click="confirmAction">
              {{ modalAction === 'approve' ? 'Approve' : 'Reject' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { authUrl, execute } from "@/api";

export default {
  name: 'ImprestApproval',
  components: { HrPageLayout },
  data() {
    return {
      search: '',
      startDate: '',
      endDate: '',
      totalRequests: 0,
      pendingApprovals: 0,
      totalPendingAmount: 0,
      isLoading: false,
      imprestRequests: [],
      currentPage: 1,
      itemsPerPage: 10,
      totalPages: 1,
      modalTitle: '',
      modalMessage: '',
      modalAction: '',
      selectedRequest: null,
      rejectionReason: '',
      showModal: false,
      searchTimeout: null
    };
  },
  computed: {
    displayPages() {
      const total = this.totalPages;
      const current = this.currentPage;
      const delta = 2;
      if (total <= 5) return Array.from({ length: total }, (_, i) => i + 1);
      let pages = [1];
      let start = Math.max(2, current - delta);
      let end = Math.min(total - 1, current + delta);
      if (start > 2) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < total - 1) pages.push('...');
      if (total > 1) pages.push(total);
      return pages;
    }
  },
  mounted() { this.getImprestRequests(); },
  watch: { search() { this.getImprestRequests(); } },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'KES', minimumFractionDigits: 2 }).format(value || 0);
    },
    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const d = new Date(dateString);
      return d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
    },
    getStatusBadgeClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'pending') return 'hr-badge hr-badge-warning';
      if (s === 'approved') return 'hr-badge hr-badge-success';
      if (s === 'rejected') return 'hr-badge hr-badge-danger';
      return 'hr-badge hr-badge-secondary';
    },
    onSearchInput() { this.getImprestRequests(); },
    approveRequest(request) {
      this.selectedRequest = request;
      this.modalTitle = 'Approve Imprest Request';
      this.modalMessage = `Are you sure you want to approve imprest request ${request.request_id}?`;
      this.modalAction = 'approve';
      this.showModal = true;
    },
    rejectRequest(request) {
      this.selectedRequest = request;
      this.rejectionReason = '';
      this.modalTitle = 'Reject Imprest Request';
      this.modalMessage = `Please provide a reason for rejecting imprest request ${request.request_id}:`;
      this.modalAction = 'reject';
      this.showModal = true;
    },
    closeModal() { this.showModal = false; },
    confirmAction() {
      if (this.modalAction === 'reject' && !this.rejectionReason.trim()) {
        this.$toast?.warning?.('Please provide a reason for rejection') || alert('Please provide a reason for rejection');
        return;
      }
      this.closeModal();
      if (this.modalAction === 'approve') this.confirmApproval();
      else this.confirmRejection();
    },
    confirmApproval() {
      const data = new FormData();
      data.append("function", "approveImprestRequest");
      data.append("requestId", this.selectedRequest.request_id);
      this.isLoading = true;
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.selectedRequest.status = 'Approved';
          this.pendingApprovals--;
          this.$toast?.success?.('Imprest request approved successfully') || alert('Imprest request approved successfully');
        } else {
          this.$toast?.error?.(res.data.message) || alert(res.data.message);
        }
      }).catch((e) => {
        this.$toast?.error?.(e.message) || alert(e.message);
      }).finally(() => { this.isLoading = false; });
    },
    confirmRejection() {
      const data = new FormData();
      data.append("function", "rejectImprestRequest");
      data.append("requestId", this.selectedRequest.request_id);
      data.append("reason", this.rejectionReason);
      this.isLoading = true;
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.selectedRequest.status = 'Rejected';
          this.pendingApprovals--;
          this.$toast?.success?.('Imprest request rejected successfully') || alert('Imprest request rejected successfully');
        } else {
          this.$toast?.error?.(res.data.message) || alert(res.data.message);
        }
      }).catch((e) => {
        this.$toast?.error?.(e.message) || alert(e.message);
      }).finally(() => { this.isLoading = false; });
    },
    getImprestRequests() {
      this.isLoading = true;
      const data = new FormData();
      data.append("function", "getImprestRequests");
      data.append("search", this.search);
      data.append("startDate", this.startDate);
      data.append("endDate", this.endDate);
      data.append("page", this.currentPage);
      data.append("perPage", this.itemsPerPage);
      execute(data, authUrl).then((res) => {
        if (res.data.success) {
          this.imprestRequests = res.data.data.imprestRequests || [];
          this.totalRequests = res.data.data.metrics?.totalRequests || 0;
          this.pendingApprovals = res.data.data.metrics?.pendingApprovals || 0;
          this.totalPendingAmount = res.data.data.metrics?.totalAmount || 0;
          this.totalPages = Math.max(1, Math.ceil(this.totalRequests / this.itemsPerPage));
        } else {
          this.$toast?.error?.(res.data.message) || alert(res.data.message);
        }
      }).catch((e) => {
        this.$toast?.error?.(e.message) || alert(e.message);
      }).finally(() => { setTimeout(() => { this.isLoading = false; }, 300); });
    },
    changePage(page) {
      if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
        this.currentPage = page;
        this.getImprestRequests();
      }
    }
  }
};
</script>

<style scoped>
@import "@/assets/css/hr-page-shared.css";

.hr-search-wrap { position: relative; }
.hr-search-wrap .hr-input { padding-right: 36px; }
.hr-search-wrap .search-icon { position: absolute; right: 12px; top: 50%; transform: translateY(-50%); color: #94a3b8; pointer-events: none; }
.hr-spinner-sm { display: inline-block; width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.3); border-top-color: #fff; border-radius: 50%; margin-left: 8px; animation: spin 0.8s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.hr-btn-group { display: flex; gap: 8px; flex-wrap: wrap; }
.hr-modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 1050; }
.hr-modal-dialog-sm { max-width: 480px; width: 90%; }
.hr-modal-content { background: #fff; border-radius: 16px; box-shadow: 0 10px 40px rgba(0,0,0,0.2); overflow: hidden; }
.hr-modal-header { padding: 20px; border-bottom: 1px solid #e2e8f0; }
.hr-modal-title { margin: 0; font-size: 1.1rem; }
.hr-modal-close { background: none; border: none; font-size: 1.5rem; cursor: pointer; color: #64748b; }
.hr-modal-body { padding: 20px; }
.hr-modal-footer { padding: 16px 20px; border-top: 1px solid #e2e8f0; }

@media (max-width: 768px) {
  .hr-chart-header { flex-direction: column; align-items: stretch; }
  .hr-search-wrap, .hr-flex input { width: 100%; max-width: none; }
}
</style>
